import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLeads, deleteLead, patchLead, getClientOrigins, getLeadStatuses, logError } from '../api';
import GenericList from './Global/GenericList';
import DisabledToggle from './ui/DisabledToggle';
import InfoToggle from './Global/InfoToggle';
import LeadCommentsRow from './LeadCommentsRow';
import LeadRegistriesRow from './LeadRegistriesRow';
import LeadFilters from './LeadFilters';
import { useDisabledToggle } from '../hooks/useDisabledToggle';
import { useSorting } from '../hooks/useSorting';
import useFilterPersistence from '../hooks/useFilterPersistence';
import { Container, Row, Col } from 'react-bootstrap';
import { isPropietarioOrAdminOrGestion, getSalesRepId } from '../services/AuthService';

const LeadList = () => {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [openComments, setOpenComments] = useState({});
    const [openRegistries, setOpenRegistries] = useState({});
    const [loadedRegistries, setLoadedRegistries] = useState({});
    const [searchFilters, setSearchFilters, resetSearchFilters] = useFilterPersistence('filters_leads', {});
    const [pagination, setPagination] = useFilterPersistence('pagination_leads', { page: 1, pageSize: 50 });
    const currentPage = pagination.page;
    const itemsPerPage = pagination.pageSize;
    const [hasPendingLeads, setHasPendingLeads] = useState(false);

    
    const { includeDisabled, setIncludeDisabled } = useDisabledToggle();
    const { orderBy, orderDesc, handleSort } = useSorting();
    const navigate = useNavigate();

    const loadLeads = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getLeads(currentPage, itemsPerPage, includeDisabled, orderBy, orderDesc, searchFilters);
            setLeads(response.items || []);
            setTotalCount(response.totalCount || 0);
            setHasPendingLeads(response.items?.some(lead => lead.hasPendingLeads) || false);
        } catch (error) {
            logError(error);
            console.error('Error loading leads:', error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, itemsPerPage, includeDisabled, orderBy, orderDesc, searchFilters]);





    useEffect(() => {
        loadLeads();
    }, [loadLeads]);



    const handleDelete = async (lead) => {
        try {
            await deleteLead(lead.id);
            loadLeads();
        } catch (error) {
            logError(error);
            console.error('Error deleting lead:', error);
        }
    };

    const canEditLead = (lead) => {
        const isSuperUser = isPropietarioOrAdminOrGestion();
        const currentSalesRepId = getSalesRepId();
        const isAssignedToCurrentUser = lead.salesRepId === currentSalesRepId;
        const hasIncompleteData = !lead.name || !lead.zipCode;
        
        return isSuperUser || (isAssignedToCurrentUser && hasIncompleteData);
    };

    const handleEdit = (lead) => {
        if (canEditLead(lead)) {
            navigate(`/lead-assignment/edit/${lead.id}`);
        }
    };

    const handleToggleStatus = async (leadId, currentStatus) => {
        try {
            await patchLead(leadId, { disabled: !currentStatus });
            loadLeads();
        } catch (error) {
            logError(error);
            console.error('Error toggling lead status:', error);
        }
    };

    const handleToggleUrgent = async (leadId, currentUrgent) => {
        try {
            const lead = leads.find(l => l.id === leadId);
            await patchLead(leadId, { 
                isUrgent: !currentUrgent,
                statusId: lead?.statusId
            });
            loadLeads();
        } catch (error) {
            logError(error);
            console.error('Error updating urgent status:', error);
        }
    };

    const handleSearch = useCallback((filters) => {
        setSearchFilters(filters);
        setPagination(prev => prev.page === 1 ? prev : { ...prev, page: 1 });
    }, [setSearchFilters, setPagination]);

    const handleResetFilters = () => {
        resetSearchFilters();
        setPagination({ ...pagination, page: 1 });
    };



    const columns = [
        { key: 'id', header: 'ID', sortable: true },
        {
            key: 'urgent',
            header: 'Urgente',
            sortable: false,
            render: (lead) => (
                <span 
                    className={`badge ${lead.isUrgent ? 'bg-warning text-dark' : 'bg-light text-muted'} urgent-toggle`}
                    onClick={() => handleToggleUrgent(lead.id, lead.isUrgent)}
                    style={{ cursor: 'pointer' }}
                >
                    <i className={`bi ${lead.isUrgent ? 'bi-lightning-fill' : 'bi-lightning'} me-1`}></i>
                    {lead.isUrgent ? 'SÍ' : 'NO'}
                </span>
            )
        },
        { key: 'name', header: 'Nombre', sortable: true },
        { key: 'phone', header: 'Teléfono', sortable: true },
        { key: 'zipCode', header: 'Código Postal', sortable: true },
        {
            key: 'clientOrigin',
            header: 'Origen',
            sortable: true,
            render: (lead) => lead.origin?.name || ''
        },
        {
            key: 'status',
            header: 'Estado',
            sortable: true,
            render: (lead) => {
                if (!lead.status?.name) return 'Sin status';
                
                const getStatusColor = (statusId) => {
                    const colors = {
                        1: '#ffc107', // Pendiente de atender - amarillo
                        2: '#17a2b8', // Seguimiento - azul
                        3: '#28a745', // Venta - verde
                        4: '#dc3545'  // No Venta - rojo
                    };
                    return colors[statusId] || '#6c757d';
                };
                
                return (
                    <span 
                        className="badge" 
                        style={{ backgroundColor: getStatusColor(lead.statusId), color: 'white' }}
                    >
                        {lead.status.name}
                    </span>
                );
            }
        },
        {
            key: 'subStatus',
            header: 'Detalle de Estado',
            sortable: true,
            render: (lead) => {
                if (!lead.subStatus?.name) return '';
                
                return (
                    <span className="badge bg-secondary">
                        {lead.subStatus.name}
                    </span>
                );
            }
        },
        { 
            key: 'salesRepId', 
            header: 'Comercial', 
            sortable: true,
            render: (lead) => lead.salesRep ? `${lead.salesRep.name} ${lead.salesRep.lastName1}` : ''
        },
        { 
            key: 'followUpDate', 
            header: 'Fecha de Seguimiento', 
            sortable: true,
            render: (lead) => lead.followUpDate ? new Date(lead.followUpDate).toLocaleDateString() : ''
        },
        { 
            key: 'createdByUserId', 
            header: 'Creado por', 
            sortable: true,
            render: (lead) => lead.createdByUser?.username || ''
        },
        { 
            key: 'createdTime', 
            header: 'Fecha Creación', 
            sortable: true,
            render: (lead) => new Date(lead.createdTime).toLocaleString('es-ES', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit', 
                hour: '2-digit', 
                minute: '2-digit' 
            })
        },
        {
            key: 'registries',
            header: 'Registros',
            sortable: false,
            render: (lead) => (
                <InfoToggle
                    label="Ver Registros"
                    openState={openRegistries}
                    handleToggle={() => setOpenRegistries(prev => ({ ...prev, [lead.id]: !prev[lead.id] }))}
                    id={lead.id}
                    color="#28a745"
                />
            )
        },
        {
            key: 'comments',
            header: 'Comentarios',
            sortable: false,
            render: (lead) => (
                <InfoToggle
                    label="Ver"
                    openState={openComments}
                    handleToggle={() => setOpenComments(prev => ({ ...prev, [lead.id]: !prev[lead.id] }))}
                    id={lead.id}
                    color="#17a2b8"
                />
            )
        }
    ];

    return (
        <Container fluid className="my-4">
            {hasPendingLeads && (
                <Row className="mb-3">
                    <Col xs={12}>
                        <div className="alert alert-danger alert-dismissible d-flex align-items-center" role="alert" style={{ fontSize: '1.1em', fontWeight: 'bold' }}>
                            <i className="bi bi-exclamation-triangle-fill me-2" style={{ fontSize: '1.5em' }}></i>
                            <div>
                                <strong>¡ATENCIÓN!</strong> Tienes leads pendientes de días pasados que requieren seguimiento inmediato. 
                                Gestiona estos leads antes de procesar nuevos.
                            </div>
                        </div>
                    </Col>
                </Row>
            )}
            <Row className="mb-3">
                <Col xs={12} className="d-flex justify-content-end">
                    <DisabledToggle 
                        includeDisabled={includeDisabled}
                        onChange={setIncludeDisabled}
                    />
                </Col>
            </Row>
            <GenericList
                title="Asignación de Leads"
                data={leads}
                columns={columns}
                loading={loading}
                totalCount={totalCount}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => setPagination({ ...pagination, page })}
                onItemsPerPageChange={(pageSize) => setPagination({ page: 1, pageSize })}
                onDelete={handleDelete}
                onEdit={handleEdit}
                onToggleStatus={handleToggleStatus}
                createRoute="/lead-assignment/new"
                createButtonText="Nuevo Lead"
                deleteMessage="¿Estás seguro de que deseas eliminar este lead?"
                emptyMessage="No se encontraron leads."
                onSort={handleSort}
                sortable={true}
                containerFluid={true}
                showSearch={true}
                SearchComponent={LeadFilters}
                searchProps={{
                    onSearch: handleSearch,
                    onResetFilters: handleResetFilters,
                    initialFilters: searchFilters
                }}
                getRowClassName={(lead) => lead.isUrgent ? 'table-warning urgent-row' : ''}
                renderExpandedRow={(lead) => {
                    const showComments = openComments[lead.id];
                    const showRegistries = openRegistries[lead.id];
                    const totalColumns = columns.length + 1; // +1 for actions column
                    
                    if (!showComments && !showRegistries) return null;
                    
                    return (
                        <tr>
                            <td colSpan={totalColumns} style={{ padding: 0, border: 'none' }}>
                                {showRegistries && <LeadRegistriesRow lead={lead} loadedRegistries={loadedRegistries} setLoadedRegistries={setLoadedRegistries} />}
                                {showComments && <LeadCommentsRow lead={lead} onUpdate={loadLeads} />}
                            </td>
                        </tr>
                    );
                }}
            />
        </Container>
    );
};

export default LeadList;